import React, { useEffect, useMemo, useState } from "react";
import userSourceScopeService from "../../services/userSourceScopeService";

function SourceScopeEditor({
  mode,
  sources,
  available,
  onChange,
  disabled = false,
}) {
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const currentSources = useMemo(
    () => (Array.isArray(sources) ? sources : []),
    [sources],
  );

  useEffect(() => {
    if (disabled) return;
    let cancelled = false;

    const scopeForPreview =
      mode === "all" ? null : mode === "none" ? [] : currentSources;

    if (mode === "specific" && currentSources.length === 0) {
      setPreviewCount(0);
      return;
    }

    setPreviewLoading(true);
    userSourceScopeService
      .preview(scopeForPreview)
      .then((res) => {
        if (!cancelled) setPreviewCount(Number(res?.count || 0));
      })
      .catch(() => {
        if (!cancelled) setPreviewCount(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, currentSources, disabled]);

  const setMode = (next) => {
    if (disabled) return;
    if (next === "specific") {
      onChange({ mode: "specific", sources: currentSources });
    } else {
      onChange({ mode: next, sources: [] });
    }
  };

  const toggleSource = (source) => {
    if (disabled) return;
    const next = currentSources.includes(source)
      ? currentSources.filter((s) => s !== source)
      : [...currentSources, source];
    onChange({ mode: "specific", sources: next });
  };

  return (
    <div className="source-scope-editor">
      <div className="source-scope-note">
        <i className="fas fa-info-circle" />
        <span>
          <strong>All Sources</strong> gives this user access to every lead in
          the CRM. Choose <strong>Specific Sources</strong> to limit the user
          to certain lead sources.
        </span>
      </div>

      <label className="source-scope-radio">
        <input
          type="radio"
          name="scope-mode"
          value="all"
          checked={mode === "all"}
          onChange={() => setMode("all")}
          disabled={disabled}
        />
        <span>All Sources (default)</span>
      </label>

      <label className="source-scope-radio">
        <input
          type="radio"
          name="scope-mode"
          value="specific"
          checked={mode === "specific"}
          onChange={() => setMode("specific")}
          disabled={disabled}
        />
        <span>Specific Sources</span>
      </label>

      {mode === "specific" && (
        <div className="source-scope-list">
          {available.length === 0 ? (
            <div className="text-muted" style={{ padding: "8px 12px" }}>
              No active lead sources configured.
            </div>
          ) : (
            available.map((source) => (
              <label key={source} className="source-scope-item">
                <input
                  type="checkbox"
                  checked={currentSources.includes(source)}
                  onChange={() => toggleSource(source)}
                  disabled={disabled}
                />
                <span>{source}</span>
              </label>
            ))
          )}
        </div>
      )}

      <label className="source-scope-radio">
        <input
          type="radio"
          name="scope-mode"
          value="none"
          checked={mode === "none"}
          onChange={() => setMode("none")}
          disabled={disabled}
        />
        <span>No Sources (user sees nothing until assigned)</span>
      </label>

      <div className="source-scope-preview">
        {previewLoading ? (
          <>
            <i className="fas fa-spinner fa-spin" /> Calculating...
          </>
        ) : previewCount === null ? (
          "Preview unavailable"
        ) : (
          <>
            <i className="fas fa-eye" />{" "}
            <strong>
              This user will see {previewCount} lead
              {previewCount === 1 ? "" : "s"}
            </strong>
          </>
        )}
      </div>
    </div>
  );
}

export default SourceScopeEditor;